import { z } from 'zod';

/**
 * Conclusions en réplique — sortie structurée du Legal Planner en mode « riposte ».
 *
 * Produite par le profil riposte de `GrokLegalPlanner.analyzeAndPlanResearch`
 * (fork `mode:'riposte'` du pipeline research-only) : au lieu de générer des
 * moyens depuis le dossier, le planner ALIGNE les écritures adverses sur NOTRE
 * écriture n°1 et classe chaque moyen en trois branches (repris/enrichi/nouveau).
 *
 * **Frontière de réutilisation** : ce `RipostePlan` est validé PUIS mappé en
 * `{ caseAnalysis, arguments: ArgumentDraft[] }` DANS le planner (avant le return),
 * pour que tout l'aval (prepareArgumentsGlobally, JP-search, aggregator, plan)
 * reste standard. La `branche` de chaque point voyage sur un canal latéral
 * (`Map<argumentId, branche>`), car `ArgumentDraftSchema` est `.strict()`.
 * Voir `docs/features/act-generation/plans/2026-07-03-conclusions-replique-simple-fork.html`.
 *
 * **Pourquoi PAS de `.strict()`** : ce schéma valide une sortie structurée de
 * LLM. Le profil riposte l'envoie en `json_object` seul (pas de json_schema
 * strict) — un `.strict()` sur un payload LLM est une classe d'incident connue
 * (#83, 2026-06-01) : le modèle ajoute spontanément une clé hors schéma et le
 * parse échoue alors que la complétion a réussi. Les champs conditionnels sont
 * rendus tolérants via `.default()` — les LLM omettent naturellement les champs
 * vides plutôt que d'émettre `""`, et exiger leur présence faisait échouer la
 * validation sur tous les candidats (parse-fail → cascade de fallback inutile).
 */

/**
 * Ce qui a DÉJÀ été mobilisé dans NOTRE écriture n°1 sur le moyen visé.
 *
 * Extrait STRICTEMENT de notre propre écriture (jamais de la JP citée par
 * l'adversaire) — c'est le matériel à reproduire/conserver à la virgule près
 * dans la réplique (reprise faite à la génération de l'acte via
 * `{{ownPreviousPleadings}}`). `null` quand la branche est `nouveau`.
 */
export const DejaMobiliseSchema = z.object({
  /** Articles / textes déjà visés dans notre écriture. */
  fondements: z.array(z.string()).default([]),
  /** Jurisprudence déjà citée dans notre écriture. */
  jurisprudence: z.array(z.string()).default([]),
  /** Pièces déjà invoquées (numéros). */
  pieces: z.array(z.string()).default([]),
  /** Résumé en une phrase de la démonstration déjà faite. */
  demonstration: z.string().default(''),
});
export type DejaMobilise = z.infer<typeof DejaMobiliseSchema>;

/**
 * Un moyen adverse, classé et instruit pour la riposte.
 *
 * - `repris`  : moyen non attaqué → simple ligne de renvoi, aucun champ de travail.
 * - `enrichi` : moyen existant attaqué → `deja_mobilise` (à conserver) + jp ciblée.
 * - `nouveau` : point neuf soulevé par l'adversaire → `nouveau_en_droit` + research complet.
 */
export const RipostePointSchema = z.object({
  /** Lettre de section : A, B, C, … */
  id: z.string(),
  /** Intitulé du moyen, ex. « Sur la prescription des charges ». */
  intitule: z.string(),
  branche: z.enum(['repris', 'enrichi', 'nouveau']),
  /** Verbatim (ou quasi) de ce que soutient l'adversaire ; `''` si repris. */
  ce_que_soutient_adversaire: z.string().default(''),
  /** Matériel de notre écriture n°1 à conserver ; `null` si branche=nouveau. */
  deja_mobilise: DejaMobiliseSchema.nullable().default(null),
  /** Requête de recherche JP ciblée (conceptuelle, jamais de n° d'arrêt) ; `''` si repris. */
  requete_jp: z.string().default(''),
  /** Angle de riposte tranché (principal + subsidiaire si pertinent) ; `''` si repris. */
  angle_retenu: z.string().default(''),
  /** Élément factuel d'application citant les pièces ; `''` si repris. */
  element_factuel: z.string().default(''),
  /** Si branche=nouveau : le bloc « En droit » ; sinon `''`. */
  nouveau_en_droit: z.string().default(''),
});
export type RipostePoint = z.infer<typeof RipostePointSchema>;

export const RiposteDossierSchema = z.object({
  position: z.string(),
  juridiction: z.string(),
  ecriture_base: z.string(),
  ecriture_adverse: z.string(),
});
export type RiposteDossier = z.infer<typeof RiposteDossierSchema>;

export const RipostePlanSchema = z.object({
  dossier: RiposteDossierSchema,
  /** Vue d'ensemble, une puce par point. */
  synthese: z.array(z.string()).default([]),
  points: z.array(RipostePointSchema),
});
export type RipostePlan = z.infer<typeof RipostePlanSchema>;
